import axios from "axios";
import { storage, firestore } from "@redux/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { withTimeout } from "@utils/apiTimeout";
import { ApiTimeoutError } from "@utils/appErrors";
import withAuthHeaders from "@utils/authHeaders";
import { validateProcessDocumentResponse, validateFeedbackResponse } from "@utils/responseValidators";
import { apriFoto, eErroreHeic } from "./fotoHeic";

// Helper per logging solo in development
const isDevelopment = process.env.REACT_APP_ENVIRONMENT === "development" || process.env.NODE_ENV === "development";
const devLog = (...args) => {
  if (isDevelopment) console.log(...args);
};
const devError = (...args) => {
  if (isDevelopment) console.error(...args);
};
const devWarn = (...args) => {
  if (isDevelopment) console.warn(...args);
};

let API_URL;

if (process.env.REACT_APP_ENVIRONMENT === "development") {
  API_URL = "http://" + process.env.REACT_APP_API_URL_DEVELOPMENT;
} else if (process.env.REACT_APP_ENVIRONMENT === "preproduction") {
  API_URL = "https://" + process.env.REACT_APP_API_URL_PREPRODUCTION;
} else {
  API_URL = "https://" + process.env.REACT_APP_API_URL_PRODUCTION;
}

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: API_URL ? API_URL.replace(/["']/g, "") : "http://localhost:8080",
  headers: {
    "Content-Type": "application/json",
  },
  // Evita gli header che causano problemi CORS
  transformRequest: [
    (data, headers) => {
      // Rimuovi gli header problematici
      if (headers) {
        delete headers.baggage;
        delete headers["sentry-trace"];
      }
      return JSON.stringify(data);
    },
  ],
});

// L'accettatore della coda documenti (STU-154): stesso corpo di
// /process-document, ma risponde 202 in millisecondi e accoda su Cloud
// Tasks. L'esito arriva dal listener su documents/{id}.processingStatus.
export const ENDPOINT_ACCETTATORE = "/process-document-streaming";

// Function to process documents
//
// `opzioni.accoda`: quando e' vero il corpo va all'accettatore invece che
// all'endpoint sincrono, e la funzione ritorna la risposta di accettazione
// (`{ accodato: true, status, jobId, ... }`), NON i materiali. Sta qui, e non
// in una funzione a parte, perche' il corpo della richiesta e' identico e
// costruirlo e' meta' di questo file: due copie divergerebbero al primo
// campo nuovo.
const processDocument = async (
  documentText,
  requestType = "",
  isPremium = false,
  customInstructions = "",
  studyMode = null,
  examDate = null,
  skipPlanner = false,
  excludedDays = [],
  opzioni = {},
) => {
  try {


    // Gestisci correttamente i diversi tipi di input
    let processedDocumentText = "";
    let requestBody = {};

    // Assicurati che isPremium sia sempre un booleano
    const premiumStatus = Boolean(isPremium);

    // Se l'input è un oggetto con document_text, usalo direttamente
    if (
      typeof documentText === "object" &&
      documentText !== null &&
      documentText.document_text
    ) {

      
      console.log(
        `Processing document - Premium: ${premiumStatus}, Type: ${requestType}, Length: ${documentText.document_text.length}, Custom Instructions: ${customInstructions ? 'Yes' : 'No'}`
      );
      // Usa l'oggetto direttamente come corpo della richiesta e assicurati che isPremium sia incluso
      requestBody = {
        ...documentText,
        isPremium: premiumStatus, // Assicuriamoci che isPremium sia incluso come booleano
        requestType: requestType || documentText.requestType, // Usa requestType se fornito, altrimenti usa quello nell'oggetto
        customInstructions: customInstructions || documentText.customInstructions || "", // Includi le istruzioni personalizzate
        skipPlanner: skipPlanner || documentText.skipPlanner || false, // Usa il parametro skipPlanner o quello nell'oggetto
        studyMode: studyMode || null, // Passa la modalità di studio solo se selezionata
        examDate: examDate || null,
        excludedDays: Array.isArray(excludedDays) && excludedDays.length > 0 ? excludedDays : undefined
      };
      // Fallback: se excludedDays vuoto, leggi da _pending_excludedDays (race condition navigazione)
      if (!requestBody.excludedDays) {
        try {
          const pending = localStorage.getItem('_pending_excludedDays');
          if (pending) {
            const parsed = JSON.parse(pending);
            if (Array.isArray(parsed) && parsed.length > 0) {
              requestBody.excludedDays = parsed;
            }
          }
        } catch (_) {}
      }

      // Se è un materiale didattico o non è stata selezionata una modalità di studio, non includere studyMode e examDate
      if (documentText.document_type === 'Materiale didattico' || !studyMode) {
        delete requestBody.studyMode;
        delete requestBody.examDate;
      }
    } else {
      // Altrimenti, crea un oggetto con documentText come document_text
      processedDocumentText =
        typeof documentText === "object"
          ? JSON.stringify(documentText)
          : documentText;
      console.log(
        `Processing document - Premium: ${premiumStatus}, Type: ${requestType}, Length: ${processedDocumentText.length}, Custom Instructions: ${customInstructions ? 'Yes' : 'No'}`
      );

      requestBody = {
        document_text: processedDocumentText,
        requestType,
        isPremium: premiumStatus,
        customInstructions: customInstructions || "",
        skipPlanner: skipPlanner,
        studyMode: studyMode || null,
        examDate: examDate || null,
        excludedDays: Array.isArray(excludedDays) && excludedDays.length > 0 ? excludedDays : undefined
      };
      // Fallback: se excludedDays vuoto, leggi da _pending_excludedDays
      if (!requestBody.excludedDays) {
        try {
          const pending = localStorage.getItem('_pending_excludedDays');
          if (pending) {
            const parsed = JSON.parse(pending);
            if (Array.isArray(parsed) && parsed.length > 0) {
              requestBody.excludedDays = parsed;
            }
          }
        } catch (_) {}
      }

      // Se non è stata selezionata una modalità di studio, non includere studyMode e examDate
      if (!studyMode) {
        delete requestBody.studyMode;
        delete requestBody.examDate;
      }
    }

    console.log("🔍 DEBUG: requestBody finale:", {
      hasDocumentText: !!requestBody.document_text,
      documentTextType: typeof requestBody.document_text,
      documentTextLength: typeof requestBody.document_text === 'string' ? requestBody.document_text.length : 'N/A',
      isPremium: requestBody.isPremium,
      requestType: requestBody.requestType,
      hasCustomInstructions: !!requestBody.customInstructions,
      skipPlanner: requestBody.skipPlanner,
      hasStudyMode: !!requestBody.studyMode,
      hasExamDate: !!requestBody.examDate
    });

    console.log(
      `Sending request to server with isPremium=${premiumStatus}, requestType=${requestType}, and customInstructions=${customInstructions ? 'provided' : 'not provided'}`,
      requestBody
    );

    // 🔧 NUOVO: Controllo di sicurezza per assicurarsi che requestBody sia un oggetto valido
    if (!requestBody || typeof requestBody !== 'object') {
      console.error('❌ requestBody non è un oggetto valido:', requestBody);
      throw new Error('Invalid request body format');
    }

    // 🔧 NUOVO: Assicurati che document_text sia sempre presente e sia una stringa
    if (!requestBody.document_text || typeof requestBody.document_text !== 'string') {
      console.error('❌ document_text mancante o non è una stringa:', requestBody.document_text);
      throw new Error('Invalid document_text format');
    }

    // 🔧 NUOVO: Crea una copia pulita del requestBody per evitare problemi di serializzazione
    const cleanRequestBody = {
      document_text: String(requestBody.document_text),
      isPremium: Boolean(requestBody.isPremium),
      requestType: requestBody.requestType ? String(requestBody.requestType) : '',
      customInstructions: requestBody.customInstructions ? String(requestBody.customInstructions) : '',
      skipPlanner: Boolean(requestBody.skipPlanner)
    };

    // Aggiungi campi opzionali solo se presenti e validi
    if (requestBody.studyMode && typeof requestBody.studyMode === 'string') {
      cleanRequestBody.studyMode = requestBody.studyMode;
    }
    if (requestBody.examDate && (requestBody.examDate instanceof Date || typeof requestBody.examDate === 'string')) {
      cleanRequestBody.examDate = requestBody.examDate;
      // Se non c'è desired_days esplicito, calcolalo dall'examDate come fallback coerente
      try {
        if (!cleanRequestBody.desired_days) {
          const today = new Date();
          const ex = new Date(requestBody.examDate);
          const diffMs = ex.getTime() - today.setHours(0,0,0,0);
          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
          if (diffDays > 0) {
            cleanRequestBody.desired_days = diffDays;
            console.log('📝 [Planner->request] desired_days calcolato da examDate:', diffDays);
          }
        }
      } catch (_) {}
    }
    if (requestBody.file_name && typeof requestBody.file_name === 'string') {
      cleanRequestBody.file_name = requestBody.file_name;
    }
    if (requestBody.document_type && typeof requestBody.document_type === 'string') {
      cleanRequestBody.document_type = requestBody.document_type;
    }
    if (requestBody.user_id && typeof requestBody.user_id === 'string') {
      cleanRequestBody.user_id = requestBody.user_id;
    }
    if (requestBody.content_uuid && typeof requestBody.content_uuid === 'string') {
      cleanRequestBody.content_uuid = requestBody.content_uuid;
    } else {
      // Fallback: usa contentUUID da localStorage se presente (garantisce correlazione stream/event-log)
      try {
        const lsCU = localStorage.getItem('streamingContentUUID');
        if (lsCU && typeof lsCU === 'string' && lsCU.trim()) {
          cleanRequestBody.content_uuid = lsCU.trim();
        }
      } catch (_) {}
    }
    // ✅ NUOVO: Includi document_id per salvare flashcards e quiz in questions collection
    if (requestBody.document_id && typeof requestBody.document_id === 'string') {
      cleanRequestBody.document_id = requestBody.document_id;
      console.log('📤 document_id incluso nel payload per il backend:', requestBody.document_id);
    } else {
      // Fallback: prova a recuperare da localStorage
      try {
        const lsDocId = localStorage.getItem('streamingDocumentId');
        if (lsDocId && typeof lsDocId === 'string' && lsDocId.trim()) {
          cleanRequestBody.document_id = lsDocId.trim();
          console.log('📤 document_id recuperato da localStorage:', lsDocId);
        }
      } catch (_) {}
    }
    if (requestBody.topicDetails && typeof requestBody.topicDetails === 'object') {
      cleanRequestBody.topicDetails = requestBody.topicDetails;
    }
    // Includi excludedDays se presenti
    if (Array.isArray(requestBody.excludedDays) && requestBody.excludedDays.length > 0) {
      cleanRequestBody.excludedDays = requestBody.excludedDays;
    } else {
      // Fallback da _pending_excludedDays
      try {
        const pending = localStorage.getItem('_pending_excludedDays');
        if (pending) {
          const parsed = JSON.parse(pending);
          if (Array.isArray(parsed) && parsed.length > 0) {
            cleanRequestBody.excludedDays = parsed;
          }
        }
      } catch (_) {}
    }

    // NUOVO: Includi giorni desiderati se l'utente li ha scelti (localStorage o requestBody)
    try {
      let desiredDays = null;
      if (typeof requestBody.desired_days === 'number' && requestBody.desired_days > 0) {
        desiredDays = requestBody.desired_days;
        console.log('📝 [Planner->request] desired_days inviato al server:', desiredDays);
      } else {
        const lsDesired = localStorage.getItem('plannerDesiredDays');
        if (lsDesired) {
          const parsed = parseInt(lsDesired, 10);
          if (!isNaN(parsed) && parsed > 0) desiredDays = parsed;
        }
      }
      if (desiredDays) {
        cleanRequestBody.desired_days = desiredDays;
      }
    } catch (_) {}

    if (opzioni?.accoda) {
      // Il documento DEVE esistere gia' e avere un id: il worker rilegge il
      // testo da documents/{id} quando non entra nel task, e senza id
      // l'accettatore risponde 413 sui testi grandi.
      if (!cleanRequestBody.document_id) {
        throw new Error('Coda documenti: manca document_id, il documento va creato prima');
      }
      const accettazione = await withTimeout(
        ({ signal }) => axiosInstance.post(ENDPOINT_ACCETTATORE, cleanRequestBody, { signal }),
        ENDPOINT_ACCETTATORE
      );
      // 202 e' la risposta normale; 200 e' tollerato perche' un accettatore
      // che risponde "already_queued" o un proxy potrebbero riscriverlo.
      if ((accettazione.status === 202 || accettazione.status === 200) && accettazione.data) {
        console.log(`Documento accodato (${accettazione.status}):`, accettazione.data);
        return { accodato: true, ...accettazione.data };
      }
      throw new Error(`Accettatore: risposta inattesa ${accettazione.status}`);
    }

    // Invio richiesta al server (con timeout)
    const response = await withTimeout(
      ({ signal }) => axiosInstance.post("/process-document", cleanRequestBody, { signal }),
      '/process-document'
    );

    if (response.status === 200 && response.data) {
      // Log dettagliato della risposta per debug
      console.log(
        `Risposta dal server ricevuta con successo, status: ${response.status}`
      );
      console.log(
        `Risposta contiene ${
          Object.keys(response.data).length
        } proprietà principali: ${Object.keys(response.data).join(", ")}`
      );

      // 🔧 NUOVO: Controlla se il server ha restituito un errore
      if (response.data.error) {
        console.error(`❌ Server error: ${response.data.error}`);
        throw new Error(`Server error: ${response.data.error}`);
      }

      // Converti formato _parts del backend al formato atteso dal frontend
      if (response.data.interrogation_parts && !response.data.interrogation_questions) {
        console.log('🔄 Conversione formato _parts → formato frontend...');
        const d = response.data;

        // Flatten interrogation_parts → interrogation_questions.qa_pairs
        // Handles both structures:
        //   premium: {"questions": [{question, answer}, ...]}
        //   free:    {"questions": [{"qa_pairs": [{question, answer}, ...]}]}
        const allQaPairs = [];
        (d.interrogation_parts || []).forEach(part => {
          const questions = part?.questions;
          if (questions && Array.isArray(questions)) {
            questions.forEach(q => {
              if (q?.question && q?.answer) {
                // Direct question object
                allQaPairs.push(q);
              } else if (q?.qa_pairs && Array.isArray(q.qa_pairs)) {
                // Nested qa_pairs wrapper (free user combiner)
                allQaPairs.push(...q.qa_pairs);
              }
            });
          }
          // Also handle part with qa_pairs directly (no questions wrapper)
          if (part?.qa_pairs && Array.isArray(part.qa_pairs) && !part?.questions) {
            allQaPairs.push(...part.qa_pairs);
          }
        });

        // Flatten flashcards_parts → qa_response.flashcards
        const allFlashcards = [];
        const allTags = [];
        (d.flashcards_parts || []).forEach(part => {
          if (part?.qa_pairs && Array.isArray(part.qa_pairs)) {
            allFlashcards.push(...part.qa_pairs);
          }
          if (part?.tags && Array.isArray(part.tags)) {
            allTags.push(...part.tags);
          }
        });

        // Flatten quiz_parts → quiz_questions
        const allQuiz = [];
        (d.quiz_parts || []).forEach(part => {
          if (part?.quiz_questions && Array.isArray(part.quiz_questions)) {
            allQuiz.push(...part.quiz_questions);
          }
        });

        // Flatten synthesia_parts → summary
        const summary = { abstract: '', sections: [] };
        (d.synthesia_parts || []).forEach(part => {
          if (part?.chapters) {
            // Orchestrator V2 format
            Object.assign(summary, part);
          } else if (part?.sections && Array.isArray(part.sections)) {
            summary.sections.push(...part.sections);
          }
          if (part?.abstract) summary.abstract = part.abstract;
          if (part?.detected_language) summary.detected_language = part.detected_language;
        });

        response.data = {
          interrogation_questions: { qa_pairs: allQaPairs },
          qa_response: { flashcards: allFlashcards, qa_pairs: allFlashcards },
          quiz_questions: allQuiz,
          summary,
          tags: allTags,
          isPremium: d.isPremium,
          ...(d.planner_parts?.length ? { planner: d.planner_parts } : {}),
          ...(d.streaming_stats ? { streaming_stats: d.streaming_stats } : {}),
          ...(d.diagram_parts ? { diagram_parts: d.diagram_parts } : {}),
          ...(d.document_analysis ? { document_analysis: d.document_analysis } : {}),
        };
        console.log('✅ Conversione completata:', {
          interrogationQs: allQaPairs.length,
          flashcards: allFlashcards.length,
          quizQs: allQuiz.length,
          summarySections: summary.sections?.length || 0
        });
      }

      // Verifica delle strutture importanti nella risposta
      if (response.data.interrogation_questions) {
        console.log(
          `Domande di interrogazione: ${
            response.data.interrogation_questions.qa_pairs?.length || 0
          }`
        );
      }

      // Verifica la struttura dei dati qa_response e in particolare delle flashcard
      if (response.data.qa_response) {
        console.log(`🃏 Struttura qa_response:`, {
          haFlashcards: !!response.data.qa_response.flashcards,
          hasQaPairs: !!response.data.qa_response.qa_pairs,
          flashcardsCount: response.data.qa_response.flashcards?.length || 0,
          qaPairsCount: response.data.qa_response.qa_pairs?.length || 0,
          flashcardsStruttura:
            response.data.qa_response.flashcards &&
            response.data.qa_response.flashcards.length > 0
              ? Object.keys(response.data.qa_response.flashcards[0])
              : [],
        });
      }

      if (response.data.quiz_questions) {
        console.log(
          `Domande quiz: ${response.data.quiz_questions.length || 0}`
        );
      }

      if (response.data.summary) {
        console.log(
          `Summary ricevuto: abstract=${!!response.data.summary
            .abstract}, sections=${response.data.summary.sections?.length || 0}`
        );
      }

      // Valida la struttura e qualità della risposta
      validateProcessDocumentResponse(response.data, '/process-document');

      return response.data;
    } else {
      console.error(`API request unexpected response:`, response);
      throw new Error(
        `API request unexpected response with status ${response.status}`
      );
    }
  } catch (error) {
    console.error("🔍 DEBUG: Error processing document - Dettagli errore:", {
      errorMessage: error.message,
      errorType: error.constructor.name,
      errorStack: error.stack,
      hasResponse: !!error.response,
      responseStatus: error.response?.status,
      responseData: error.response?.data
    });
    console.error("Error processing document:", error);
    throw error;
  }
};

// Function to get feedback
// documentLanguage: lingua del documento (IT, EN, ES) rilevata durante il processing
const getFeedback = async (userResponse, currentQuestion, openAIAnswer, detectedLanguage = null, documentLanguage = null, filterTopic = null, feedbackLanguage = null) => {
  try {
    if (!currentQuestion || !openAIAnswer) {
      throw new Error(
        "La domanda corrente o la risposta di OpenAI non possono essere vuote."
      );
    }

    const requestBody = { userResponse, currentQuestion, openAIAnswer };

    if (detectedLanguage) {
      requestBody.detectedLanguage = detectedLanguage;
    }

    if (documentLanguage) {
      requestBody.documentLanguage = documentLanguage;
    }

    if (filterTopic) {
      requestBody.filterTopic = filterTopic;
    }

    // Lingua del tutor (STU-529); senza, il server resta in italiano.
    if (feedbackLanguage) {
      requestBody.feedbackLanguage = feedbackLanguage;
    }

    const response = await withTimeout(
      ({ signal }) => axiosInstance.post(
        "/get-feedback",
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
          signal,
        }
      ),
      '/get-feedback'
    );

    if (response.status === 200) {
      validateFeedbackResponse(response.data, '/get-feedback');
      return response.data;
    } else {
      throw new Error(`API request failed with status ${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

/**
 * La stessa valutazione di getFeedback, ma mentre nasce.
 *
 * Non toglie un secondo al modello: la scheda completa arriva quando arrivava
 * prima. Cambia il momento in cui lo studente vede qualcosa. Il voto e' il
 * primo campo dello schema, quindi esce entro i primi token — misurato il
 * 01/09/2026 su tre domande vere: schermo vuoto per 7,9 s di mediana sulla
 * rotta intera, 1,8 s qui.
 *
 * Se il server risponde 409 (stream spento) o cade prima di scrivere
 * qualunque cosa, solleva: chi chiama rifa' la domanda sulla rotta di sempre,
 * che non e' mai stata toccata.
 *
 * @param {Object} corpo               stesso corpo di /get-feedback
 * @param {Function} onPezzo           riceve la scheda parziale a ogni campo che si chiude
 * @returns {Promise<{feedback: Object, score: number, metriche: Object}>}
 */
const getFeedbackStream = async (corpo, onPezzo) => {
  const base = axiosInstance.defaults.baseURL || '';
  const risposta = await fetch(`${base}/get-feedback-stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corpo),
  });

  if (!risposta.ok || !risposta.body) {
    throw new Error(`stream non disponibile (${risposta.status})`);
  }

  const lettore = risposta.body.getReader();
  const decoder = new TextDecoder();
  let avanzo = '';
  let finale = null;

  // La scheda si riempie in ordine di lettura: voto, positives, negatives,
  // suggestions, riassunto. E' lo stesso ordine in cui FeedbackCard la mostra,
  // quindi il campo compare dove lo studente sta gia' guardando.
  //
  // I blocchi non ancora arrivati restano ASSENTI, non vuoti: FeedbackCard
  // mostra "nessun punto positivo" quando riceve un oggetto vuoto, e quella
  // riga comparirebbe per un secondo prima di essere smentita. Un campo che
  // non c'e' ancora non si annuncia.
  const parziale = {};
  let mostrato = false;

  const applica = (evento) => {
    if (evento.tipo === 'punteggio') {
      parziale.score = evento.punteggio;
    } else if (evento.tipo === 'campo') {
      const [blocco, punto] = evento.campo.split('.');
      if (punto) {
        if (!parziale[blocco]) parziale[blocco] = {};
        parziale[blocco][punto] = evento.testo;
      } else {
        parziale[blocco] = evento.testo;
      }
    } else {
      return;
    }
    mostrato = true;
    if (onPezzo) {
      const copia = { ...parziale };
      if (parziale.positives) copia.positives = { ...parziale.positives };
      if (parziale.negatives) copia.negatives = { ...parziale.negatives };
      onPezzo(copia);
    }
  };

  while (true) {
    const { done, value } = await lettore.read();
    if (done) break;

    avanzo += decoder.decode(value, { stream: true });
    const righe = avanzo.split('\n');
    avanzo = righe.pop() || '';

    for (const riga of righe) {
      if (!riga.startsWith('data: ')) continue;
      let evento;
      try {
        evento = JSON.parse(riga.slice(6));
      } catch (e) {
        devLog('SSE feedback, riga saltata:', riga);
        continue;
      }
      if (evento.tipo === 'errore') {
        // Mezza valutazione non si tiene. Se lo studente non aveva ancora
        // visto niente, chi chiama ripiega sulla rotta intera; se stava gia'
        // leggendo, l'errore glielo diciamo invece di far comparire una
        // seconda scheda diversa sopra la prima.
        const errore = new Error('stream_interrotto');
        errore.giaMostrato = mostrato;
        throw errore;
      }
      if (evento.tipo === 'fine') {
        finale = evento;
      } else {
        applica(evento);
      }
    }
  }

  if (!finale) {
    const errore = new Error('stream chiuso senza la scheda finale');
    errore.giaMostrato = mostrato;
    throw errore;
  }

  if (finale.metriche) devLog('[feedback] metriche:', finale.metriche);
  return finale;
};

const getFeedbackWithAssistant = async (questionsArray, language = null) => {
  try {
    const response = await axiosInstance.post(
      "/get-feedback-with-assistant",
      // Lingua della valutazione (STU-529): senza, l'input al modello resta quello di prima.
      language ? { questions: questionsArray, language } : { questions: questionsArray },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`API request failed with status ${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

// Function to transcribe audio - Backend processing con base64
const transcribeAudio = async (audioBlob) => {
  try {
    console.log(`🎙️ Preparazione audio per backend: ${audioBlob.size} bytes`);
    
    // ✅ Converti Blob in base64 per invio JSON (evita problemi FormData/proxy)
    const base64Audio = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Rimuovi prefix "data:audio/mpeg;base64," se presente
        const base64 = reader.result.split(',')[1] || reader.result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(audioBlob);
    });
    
    console.log(`🎙️ Invio audio al backend: ${base64Audio.length} chars base64`);

    // Invia come JSON invece di FormData
    const response = await axiosInstance.post(
      "/transcribe-audio",
      {
        audio_base64: base64Audio,
        mime_type: audioBlob.type || 'audio/mpeg'
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.status === 200 && response.data.success) {
      console.log(`✅ Trascrizione completata: ${response.data.text.length} caratteri`);
      return { text: response.data.text };
    } else {
      console.error("API request failed with status:", response.status);
      throw new Error(`API request failed with status ${response.status}`);
    }
  } catch (error) {
    console.error("❌ Errore trascrizione audio:", error);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
      console.error("Response headers:", error.response.headers);
      // Il server dice PERCHE' (422 "Nessun parlato riconosciuto nella
      // registrazione"), axios lo copre con "Request failed with status code
      // 422". Il 23/09 il perche' si perdeva qui: ora chi chiama riceve il
      // testo del server e il codice HTTP, e sa cosa dire allo studente
      // (STU-424, utils/erroreEstrazione.js).
      const dettaglio = error.response.data?.detail;
      const errore = new Error(
        typeof dettaglio === "string" && dettaglio.trim() ? dettaglio : error.message
      );
      errore.status = error.response.status;
      errore.response = error.response;
      throw errore;
    }
    throw error;
  }
};

const processImage = async (imageFile) => {
  try {
    // Ridimensionamento per OCR: 1536px lato lungo. A 640px appunti manoscritti
    // e foto di pagine perdevano caratteri (OCR degradato); 1536 mantiene il
    // testo leggibile restando dentro i limiti Gemini.
    let base64Image;
    const MAX_DIMENSION = 1536;

    // SEMPRE comprimi (per normalizzare dimensione input Gemini)
    const shouldCompress = true;
    if (shouldCompress) {
      console.log(`⚡ Compressione intelligente: ${(imageFile.size / 1024 / 1024).toFixed(1)}MB → ${MAX_DIMENSION}px per OCR`);
      
      // Crea un canvas per ridimensionare l'immagine
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Carica l'immagine (le HEIC che il browser non apre si convertono, STU-450)
      const { img, rilascia } = await apriFoto(imageFile);

      // Calcola le nuove dimensioni mantenendo l'aspect ratio
      let width = img.width;
      let height = img.height;
      
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      // ⚡ Compressione JPEG aggressiva per velocità
      base64Image = canvas.toDataURL('image/jpeg', 0.60).split(',')[1]; // Qualità 0.60 (ottimo per OCR!)
      
      // Cleanup
      rilascia();

    } else {
      // Convert image to base64 normalmente
      base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(",")[1]);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(imageFile);
      });
    }

    // Determina il MIME type dell'immagine. Il canvas produce sempre JPEG:
    // una HEIC compressa non deve partire dichiarandosi image/heic.
    let mimeType = shouldCompress ? 'image/jpeg' : (imageFile.type || 'image/jpeg');
    if (!mimeType.startsWith('image/')) {
      mimeType = 'image/jpeg';
    }

    const requestBody = {
      image_data: base64Image,
      mime_type: mimeType,
      // custom_prompt opzionale, usa il default del server
    };

    // Usa il nuovo endpoint del server invece di chiamare OpenAI direttamente
    const response = await axios.post(
      `${API_URL}/api/process-image`,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 30000, // 30 secondi timeout
      }
    );

    if (
      response.status === 200 &&
      response.data.success &&
      response.data.extracted_text
    ) {
      const extractedText = response.data.extracted_text;
      console.log(`✅ Testo estratto con successo tramite server (${extractedText.length} caratteri)`);
      return extractedText;
    } else if (response.data.error) {
      console.error("Errore dal server:", response.data.error);
      throw new Error(response.data.error);
    } else {
      console.error("API request unexpected response:", response);
      throw new Error(
        `API request unexpected response with status ${response.status}`
      );
    }
  } catch (error) {
    console.error("Error processing image:", error);

    // La HEIC che non si apre passa intera: chi chiama la riconosce e dice
    // allo studente cosa fare, invece di «riprova piu' tardi».
    if (eErroreHeic(error)) throw error;

    // Gestione errori specifici
    if (error.response?.status === 413) {
      throw new Error("Immagine troppo grande. Il sistema ha tentato di comprimerla ma è ancora troppo pesante.");
    } else if (error.response?.status === 429) {
      throw new Error("Troppe richieste. Attendi qualche secondo e riprova.");
    } else if (error.code === 'ECONNABORTED') {
      throw new Error("Timeout nella richiesta. L'immagine potrebbe essere troppo complessa.");
    }
    
    throw new Error(
      error.response?.data?.error?.message ||
        error.message ||
        "Errore sconosciuto durante l'elaborazione dell'immagine"
    );
  }
};

const generateSpeech = async (text, options = {}) => {
  try {
    // Impostazioni predefinite
    const defaultOptions = {
      // Nessuna voce: la sceglie il server (TTS_VOICE / VOCE_DEFAULT). Qui
      // c'era "nova" fissa, e siccome questa funzione e' il ripiego del
      // percorso progressivo, ogni ripiego riportava la voce vecchia.
      voice: null,
      speed: 1.0, // Velocità normale
      accent: null,
      emotional_range: "medium",
      intonation: "natural",
      impressions: null,
      tone: "professional",
    };

    // Unisci le opzioni predefinite con quelle fornite
    const settings = { ...defaultOptions, ...options };

    // Costruisci l'oggetto voice_settings
    const voice_settings = {
      speed: settings.speed,
    };

    // Costruisci un prompt per controllare altri aspetti della voce
    let prompt = null;
    if (
      settings.accent ||
      settings.emotional_range ||
      settings.intonation ||
      settings.impressions ||
      settings.tone
    ) {
      prompt = `Speak with ${settings.tone || "professional"} tone`;

      if (settings.accent) {
        prompt += `, using a ${settings.accent} accent`;
      }

      if (settings.emotional_range) {
        prompt += `, with ${settings.emotional_range} emotional range`;
      }

      if (settings.intonation) {
        prompt += `, and ${settings.intonation} intonation`;
      }

      if (settings.impressions) {
        prompt += `. ${settings.impressions}`;
      }

      prompt += ".";
    }

    // Costruisci la richiesta
    const requestBody = {
      model: "gpt-4o-mini-tts",
      voice: settings.voice,
      input: text,
      // Forza formato mp3 per massima compatibilità browser
      format: "mp3",
      voice_settings: voice_settings,
    };

    // Aggiungi il prompt se presente
    if (prompt) {
      requestBody.prompt = prompt;
    }

    // Genera audio tramite il backend per evitare problemi di CORS/API key client-side
    const corpo = { text: requestBody.input };
    if (requestBody.voice) corpo.voice = requestBody.voice;
    // Senza lingua il server legge con le istruzioni italiane (STU-529).
    if (settings.lingua) corpo.lingua = settings.lingua;

    const response = await axiosInstance.post("/tts", corpo, {
      responseType: "arraybuffer",
    });

    return response.data;
  } catch (error) {
    console.error("Error generating speech:", {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
    });
    throw error;
  }
};

/**
 * Generate a multi-episode outline for a large document.
 * Returns series title, episode titles, summaries, key topics, and text chunks.
 */
const generatePodcastOutline = async (text, perspective = "fundamentals") => {
  try {
    devLog("Generating podcast outline for text of length:", text.length);

    const response = await withTimeout(
      ({ signal }) => axiosInstance.post("/generate-podcast-outline", { text, perspective }, { signal }),
      '/generate-podcast-outline'
    );

    devLog("Outline response received:", response.status);
    return response.data;
  } catch (error) {
    devError("Error generating podcast outline:", error);
    throw error;
  }
};

/**
 * Generate podcast via SSE endpoint with real-time progress updates.
 * @param {Object} params - { text, documentId, documentTitle, userId, config, onProgress }
 * @param {Function} params.onProgress - Called with { progress: 0-100, step: string, detail?: string }
 * @returns {Object} Podcast data with audioUrl, transcript, title, etc.
 */
const generatePodcastSSE = async ({ text, documentId, documentTitle, userId, config = {}, onProgress }) => {
  const payload = {
    text,
    multiHost: config.multiHost || false,
    voice: config.voice || 'nova',
    speed: config.speed || 1.0,
    duration: config.duration || 10,
    perspective: config.perspective || 'fundamentals',
    optimized: true,
    document_id: documentId || null,
    // Il proprietario, perche' d'ora in poi e' il SERVER a scrivere il record
    // su podcasts/{uid}/userPodcasts. Senza, il backend non sa sotto chi
    // metterlo e ricade sul vecchio comportamento (lo scrive questo file).
    user_id: userId || (getAuth().currentUser ? getAuth().currentUser.uid : null),
  };
  if (config.series_id) payload.series_id = config.series_id;
  if (config.episode_number != null) payload.episode_number = config.episode_number;
  if (config.total_episodes != null) payload.total_episodes = config.total_episodes;
  if (config.episode_title) payload.episode_title = config.episode_title;
  if (config.series_title) payload.series_title = config.series_title;
  if (config.episode_topics) payload.episode_topics = config.episode_topics;
  if (config.previous_episode_summary) payload.previous_episode_summary = config.previous_episode_summary;

  // Use fetch for SSE (supports POST, unlike EventSource)
  // Use same base URL as axiosInstance to bypass proxy issues
  const sseBaseUrl = axiosInstance.defaults.baseURL || '';

  // Il token e' l'unica identita' di cui il server possa fidarsi: `user_id`
  // nel corpo lo dichiara chiunque. Non e' bloccante di proposito — se il
  // token non c'e' (sessione scaduta a meta' pagina) il podcast si genera
  // comunque, e vale il vecchio percorso in cui il record lo scrive il client.
  const intestazioni = { 'Content-Type': 'application/json' };
  try {
    const utente = getAuth().currentUser;
    if (utente) intestazioni.Authorization = `Bearer ${await utente.getIdToken()}`;
  } catch (e) {
    devLog('Token non disponibile per /generate-podcast-sse:', e?.message);
  }

  const response = await fetch(`${sseBaseUrl}/generate-podcast-sse`, {
    method: 'POST',
    headers: intestazioni,
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new Error(`SSE request failed: ${response.status}`);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let finalData = null;
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete line in buffer

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const event = JSON.parse(line.slice(6));
        if (event.step === 'complete') {
          finalData = event.data;
          if (onProgress) onProgress({ progress: 100, step: 'complete' });
        } else if (event.step === 'error') {
          throw new Error(event.detail || 'Errore generazione podcast');
        } else {
          if (onProgress) onProgress(event);
        }
      } catch (parseErr) {
        if (parseErr.message?.includes('Errore')) throw parseErr;
        devLog("SSE parse skip:", line);
      }
    }
  }

  if (!finalData) throw new Error('Generazione podcast interrotta');

  // Persist the URL exactly as the backend returns it:
  // - prod / Firebase Storage path: an absolute https:// URL (persistent)
  // - dev / memory-store fallback: a relative path like "/podcast-audio/{id}"
  // Prefixing the relative path with the current sseBaseUrl (e.g. localhost:8080
  // in dev) and writing that to Firestore would poison the document: anyone
  // reading the same podcast from a different origin (e.g. www.studierai.app)
  // would hit a loopback URL and Chrome would block it via Private Network
  // Access. We resolve relative URLs at play-time using the current API_URL
  // instead — see PodcastPlayer.
  const effectiveUserId = userId || (getAuth().currentUser ? getAuth().currentUser.uid : null);
  const filename = finalData.audio_filename || `podcast_${Date.now()}.mp3`;
  const audioUrl = finalData.audio_url;

  const normalizedConfig = {
    multiHost: config.multiHost || false,
    voice: config.voice || 'nova',
    speed: config.speed || 1.0,
    duration: config.duration || 10,
    style: config.style || 'conversational',
    perspective: config.perspective || 'fundamentals',
  };

  const podcastData = {
    audioUrl,
    filename,
    createdAt: serverTimestamp(),
    documentId: documentId || null,
    title: finalData.title || documentTitle || 'Podcast',
    textExcerpt: (finalData.document_text || '').substring(0, 500),
    transcript: finalData.transcript,
    userId: effectiveUserId,
    config: normalizedConfig,
    multiHost: normalizedConfig.multiHost,
    voice: normalizedConfig.voice,
    duration: normalizedConfig.duration,
    seriesId: config.series_id || null,
    episodeNumber: config.episode_number != null ? config.episode_number : null,
    episodeSummary: finalData.episode_summary || null,
    // Durata LETTA dall'audio dal server. Il campo `duration` qui sopra e' la
    // durata *richiesta*: il backend fa min(duration, 10), quindi una scheda
    // "20 min" annunciava un audio che ne durava 10 — e in una misura del
    // 19/08/2026 un podcast "da 10 minuti" durava 107 secondi.
    ...(finalData.duration_seconds
      ? { durationSeconds: finalData.duration_seconds }
      : {}),
  };

  // IL SERVER HA GIA' SCRITTO IL RECORD: qui non si scrive niente.
  //
  // Finche' la riga su Firestore la scriveva questo file, un podcast esisteva
  // solo se il browser restava aperto fino alla fine: chiudi la scheda a meta'
  // e il server aveva gia' pagato script e sintesi vocale, l'MP3 era gia' su
  // Storage, e nessuna query lo trovava piu'. Due schede aperte generavano due
  // podcast e li pagavano entrambi.
  //
  // `podcast_id` e' il segnale che il record c'e' gia'. Quando manca — client
  // vecchio, identita' non risolvibile, Firestore giu' lato server — vale il
  // percorso di prima. E' cosi' che le due meta' si possono spedire separate.
  if (finalData.podcast_id) {
    return { success: true, audioUrl, firestore_id: finalData.podcast_id, ...podcastData };
  }

  if (effectiveUserId) {
    try {
      const userPodcastsRef = collection(firestore, "podcasts", effectiveUserId, "userPodcasts");
      const docRef = await addDoc(userPodcastsRef, podcastData);
      return { success: true, audioUrl, firestore_id: docRef.id, ...podcastData };
    } catch (e) {
      devError("Firestore save error:", e);
    }
  }
  return { success: true, audioUrl, ...podcastData };
};


const generatePodcast = async (
  text,
  documentId = null,
  documentTitle = null,
  userId = null,
  skipFirebaseUpload = false, // Nuovo parametro opzionale
  config = {} // Aggiunto parametro config per impostazioni podcast
) => {
  try {
    devLog(
      "Inizio generazione podcast con testo di lunghezza:",
      text.length
    );

    // Normalizza il config con tutti i campi necessari
    const normalizedConfig = {
      multiHost: config.multiHost || false,
      voice: config.voice || 'nova',
      speed: config.speed || 1.0,
      duration: config.duration || 10,
      style: config.style || 'conversational',
      perspective: config.perspective || 'fundamentals'
    };

    // Log del payload per debug
    const payload = {
      text: text,
      multiHost: normalizedConfig.multiHost,
      voice: normalizedConfig.voice,
      speed: normalizedConfig.speed,
      duration: normalizedConfig.duration,
      perspective: normalizedConfig.perspective,
      optimized: config.optimized || false, // A/B switch: true = optimized pipeline
      document_id: documentId || null
    };

    // Add series/episode fields if present
    if (config.series_id) payload.series_id = config.series_id;
    if (config.episode_number != null) payload.episode_number = config.episode_number;
    if (config.total_episodes != null) payload.total_episodes = config.total_episodes;
    if (config.episode_title) payload.episode_title = config.episode_title;
    if (config.series_title) payload.series_title = config.series_title;
    if (config.episode_topics) payload.episode_topics = config.episode_topics;
    if (config.previous_episode_summary) payload.previous_episode_summary = config.previous_episode_summary;
    devLog("Payload inviato al server:", payload);
    devLog("URL completo:", axiosInstance.defaults.baseURL + "/generate-podcast");

    // Utilizzo l'URL relativo che verrà gestito dal proxy, con timeout di 5 minuti
    const response = await withTimeout(
      ({ signal }) => axiosInstance.post("/generate-podcast", payload, { signal }),
      '/generate-podcast'
    );

    devLog("Risposta ricevuta dal server:", response.status);

    // LEGACY PATH: base64 audio nel JSON
    if (response.data && response.data.audio_base64) {
      try {
        // Converti il base64 in un Blob
        const byteCharacters = atob(response.data.audio_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const audioBlob = new Blob([byteArray], { type: "audio/mp3" });

        // Se skipFirebaseUpload è true, ritorna solo l'URL locale
        if (skipFirebaseUpload) {
          const localUrl = URL.createObjectURL(audioBlob);
          return {
            success: true,
            audio_url: localUrl,
            local_only: true,
            config: normalizedConfig, // Usa config normalizzato
            filename: response.data.audio_filename || `podcast_${Date.now()}.mp3`,
            document_id: documentId,
            document_title: documentTitle,
            transcript: response.data.podcast_transcript || response.data.transcript || response.data.document_text || null, // Ora document_text contiene la trascrizione
            episode_summary: response.data.episode_summary || null
          };
        }

        // Verifica che l'utente sia autenticato prima di caricare su Firebase
        const auth = getAuth();
        const currentUser = auth.currentUser;

        // Utilizziamo l'ID utente passato come parametro se disponibile, altrimenti usiamo currentUser
        const effectiveUserId =
          userId || (currentUser ? currentUser.uid : null);

        if (!effectiveUserId) {
          devError(
            "Utente non autenticato. Impossibile salvare su Firebase."
          );
          // Restituisci comunque l'audio ma senza salvarlo su Firebase
          const podcastData = {
            success: true,
            audio_url: URL.createObjectURL(audioBlob),
            local_only: true,
            config: normalizedConfig,
          };

          return podcastData;
        }

        // Carica il file su Firebase Storage
        const filename =
          response.data.audio_filename || `podcast_${Date.now()}.mp3`;
        const storagePath = `podcasts/${effectiveUserId}/${filename}`;
        const storageRef = ref(storage, storagePath);

        devLog(
          "Tentativo di caricamento su Firebase Storage:",
          storageRef
        );

        // Carica il blob su Firebase Storage
        const uploadResult = await uploadBytes(storageRef, audioBlob);
        devLog("Upload completato:", uploadResult);

        // Ottieni l'URL di download
        const downloadURL = await getDownloadURL(uploadResult.ref);
        devLog("URL di download ottenuto:", downloadURL);

        try {
          // Estrai il transcript dalla risposta (supporta multiple nomenclature dal backend)
          const transcript = response.data.podcast_transcript ||
                           response.data.transcript ||
                           response.data.document_text ||
                           null;

          // Estrai titolo dinamico generato dal backend (se disponibile)
          const podcastTitle = response.data.title || documentTitle || 'Podcast senza titolo';

          // Salva i metadati nella sottocollezione podcasts/{userId}/userPodcasts (struttura 2025)
          const podcastData = {
            audioUrl: downloadURL, // Cambiato da audio_url per consistenza
            // Path nel bucket: usato per rigenerare il downloadURL quando il
            // token di accesso del link salvato scade (signed URL ~7 giorni).
            storagePath: storagePath,
            filename: filename,
            createdAt: serverTimestamp(), // Cambiato da created_at
            documentId: documentId || null, // Cambiato da document_id
            title: podcastTitle, // Titolo dinamico generato da AI o fallback a documentTitle
            textExcerpt: response.data.document_text || text.substring(0, 500), // Cambiato da text_excerpt
            transcript: transcript, // Transcript completo
            userId: effectiveUserId, // Cambiato da user_id
            config: normalizedConfig, // Usa il config normalizzato con tutti i campi
            multiHost: normalizedConfig.multiHost, // Campo esplicito per query
            voice: normalizedConfig.voice, // Campo esplicito per visualizzazione
            duration: response.data.duration || normalizedConfig.duration, // Durata in minuti
            // Series fields (null for standalone podcasts — backward compatible)
            seriesId: config.series_id || null,
            episodeNumber: config.episode_number != null ? config.episode_number : null,
            episodeSummary: response.data.episode_summary || null,
          };

          devLog("Tentativo di salvataggio in Firestore (sottocollezione):", {
            ...podcastData,
            transcript: transcript ? `${transcript.substring(0, 100)}... (${transcript.length} chars)` : 'null'
          });

          // Salva nella sottocollezione generando sempre un ID univoco per ogni podcast
          // Questo permette di salvare multipli podcast per lo stesso documento (es. con config diversi)
          const userPodcastsRef = collection(firestore, "podcasts", effectiveUserId, "userPodcasts");
          const docRef = await addDoc(userPodcastsRef, podcastData);
          devLog("Documento salvato in Firestore sottocollezione con ID autogenerato:", docRef.id);

          // Crea l'oggetto podcast completo
          const completePodcastData = {
            success: true,
            audioUrl: downloadURL, // Cambiato da audio_url
            firestore_id: docRef.id,
            ...podcastData, // Già contiene config normalizzato
          };

          // Redux verrà gestito dal chiamante (useGetPodcast)

          // Restituisci i dati del podcast con l'URL di Firebase
          return completePodcastData;
        } catch (firestoreError) {
          devError("Errore nel salvataggio su Firestore:", firestoreError);

          // Messaggio di errore specifico per problemi di permessi
          let errorMessage = "Audio salvato su Storage ma non su Firestore";

          if (
            firestoreError.code === "permission-denied" ||
            firestoreError.message.includes(
              "Missing or insufficient permissions"
            )
          ) {
            errorMessage =
              "Audio salvato correttamente su Firebase Storage e disponibile per l'ascolto. Non è stato possibile salvare i metadati su Firestore per permessi insufficienti, ma questo non influisce sulla riproduzione del podcast.";
          }

          // Estrai il transcript dalla risposta per includerlo anche in caso di errore
          const transcriptForError = response.data.podcast_transcript ||
                                    response.data.transcript ||
                                    response.data.document_text ||
                                    null;

          // Crea l'oggetto podcast con errore
          const errorPodcastData = {
            success: true,
            audio_url: downloadURL,
            storage_only: true,
            error_message: errorMessage,
            config: normalizedConfig,
            // Aggiungi il blob audio per consentire il download diretto
            audio_blob: audioBlob,
            filename: filename,
            document_id: documentId,
            document_title: documentTitle,
            transcript: transcriptForError, // Includi transcript anche in caso di errore Firestore
          };

          // Redux verrà gestito dal chiamante (useGetPodcast)

          // Se fallisce il salvataggio su Firestore, restituisci comunque l'URL dell'audio
          return errorPodcastData;
        }
      } catch (storageError) {
        devError(
          "Errore nel caricamento su Firebase Storage:",
          storageError
        );

        // Crea un URL locale per l'audio
        const byteCharacters2 = atob(response.data.audio_base64);
        const byteNumbers2 = new Array(byteCharacters2.length);
        for (let i = 0; i < byteCharacters2.length; i++) {
          byteNumbers2[i] = byteCharacters2.charCodeAt(i);
        }
        const byteArray2 = new Uint8Array(byteNumbers2);
        const fallbackBlob = new Blob([byteArray2], { type: "audio/mp3" });
        const localUrl = URL.createObjectURL(fallbackBlob);

        // Crea l'oggetto podcast con errore di storage
        const localPodcastData = {
          success: true,
          audio_url: localUrl,
          local_only: true,
          error_message:
            "Impossibile salvare su Firebase: " + storageError.message,
          config: normalizedConfig,
          filename: `podcast_${Date.now()}.mp3`,
          document_id: documentId,
          document_title: documentTitle,
        };

        // Redux verrà gestito dal chiamante (useGetPodcast)

        // Restituisci un URL locale per l'audio
        return localPodcastData;
      }
    } else {
      // Controllo aggiuntivo per verificare se la risposta contiene un messaggio di errore
      if (response.data && response.data.error) {
        throw new Error(`Errore del server: ${response.data.error}`);
      } else {
        throw new Error("Risposta del server non valida: dati audio mancanti");
      }
    }
  } catch (error) {
    devError("Error generating podcast:", error);

    // Logging dettagliato dell'errore per facilitare il debug
    if (error.response) {
      // La richiesta è stata effettuata e il server ha risposto con un codice di stato
      // che non rientra nell'intervallo 2xx
      devError("Errore di risposta dal server:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      });

      // Se il server ha inviato un messaggio di errore, lo utilizziamo
      if (error.response.data && error.response.data.error) {
        throw new Error(`Errore del server: ${error.response.data.error}`);
      }
    } else if (error.request) {
      // La richiesta è stata effettuata ma non è stata ricevuta alcuna risposta
      devError("Nessuna risposta ricevuta dal server");
      throw new Error(
        "Impossibile contattare il server per la generazione del podcast"
      );
    }

    throw error;
  }
};

// Feature flag per abilitare gradualmente il nuovo sistema batch
const isNewBatchSystemEnabled = () => {
  // Controllo feature flag da localStorage per override manuale
  const localFlag = localStorage.getItem('enableNewBatchOCR');

  // Se il flag locale è 'false', disabilita (per emergenze/rollback)
  if (localFlag === 'false') {
    console.log('⚠️ Sistema batch OCR disabilitato manualmente via localStorage');
    return false;
  }

  // ABILITATO PER TUTTI GLI UTENTI IN TUTTI GLI AMBIENTI
  // Il nuovo sistema è più veloce (3-5x) e riduce il carico sui dispositivi client
  console.log('🚀 Sistema batch OCR ottimizzato attivo per tutti gli utenti');
  return true;
};

// ─────────────────────────────────────────────────────────────────────────
// L'OCR A LOTTI
//
// Prima `processMultipleImages` metteva TUTTE le immagini in un JSON solo:
// contro il tetto di 100 immagini per richiesta del server, e contro i 32 MiB
// di corpo di Cloud Run. Una scansione da 150 pagine non passava mai. Ora le
// immagini viaggiano a lotti, e il lotto N+1 viene preparato mentre il lotto
// N e' in volo, cosi' la rete e il rendering si sovrappongono.
//
// La sorgente e' di due forme:
//   - un array di File (le foto degli appunti): si ridimensionano qui;
//   - `{ totale, prendiLotto(da, a) }`: le pagine di un PDF, che il worker di
//     lettura rende in JPEG un lotto alla volta, cosi' non stanno mai tutte
//     in memoria insieme.
// I testi tornano ricuciti nell'ordine delle pagine.
// ─────────────────────────────────────────────────────────────────────────

// Meta' del tetto del server (100): a ~150 KB per pagina in base64 sono
// ~10 MB per richiesta, con margine largo sotto i 32 MiB di Cloud Run.
const PAGINE_PER_LOTTO = 50;

// Sopra questa quota di pagine perse il documento non e' piu' il documento:
// stessa soglia del server per i blocchi (`MAX_LOST_PIECES_RATIO`).
const QUOTA_MASSIMA_PAGINE_PERSE = 0.5;

const SEPARATORE_PAGINA = (numero) => `\n\n--- Pagina ${numero} ---\n\n`;

/** Il tetto (escluso) di un lotto che parte da `da`. */
const fineLotto = (da, totale, taglia = PAGINE_PER_LOTTO) => Math.min(da + taglia - 1, totale);

/**
 * Ricuce i testi delle pagine nell'ordine delle pagine.
 * @param {Map<number,string>} testi pagina -> testo
 */
const ricuciPagine = (testi) => {
  const numeri = [...testi.keys()].sort((a, b) => a - b);
  return numeri
    .map((numero, indice) => (indice > 0 ? SEPARATORE_PAGINA(numero) : '') + testi.get(numero))
    .join('');
};

/**
 * Trasforma un array di File nella forma a lotti: ogni lotto ridimensiona le
 * sue immagini a 1536 px lato lungo e le comprime in JPEG, come faceva
 * `prepareImagesForBatch` su tutte insieme.
 */
const sorgenteDaFile = (imageFiles, onProgress) => ({
  totale: imageFiles.length,
  prendiLotto: async (da, a) => {
    const pagine = [];
    for (let numero = da; numero <= a; numero++) {
      if (onProgress) {
        onProgress({
          phase: 'preparing',
          progress: Math.round(((numero - 1) / imageFiles.length) * 100),
          message: `Preparazione immagine ${numero} di ${imageFiles.length}`
        });
      }
      const file = imageFiles[numero - 1];
      pagine.push({ pagina: numero, image_data: await comprimiImmagine(file), mime_type: 'image/jpeg' });
    }
    return pagine;
  },
});

const normalizzaSorgente = (sorgente, onProgress) => {
  if (Array.isArray(sorgente)) return sorgenteDaFile(sorgente, onProgress);
  if (sorgente && typeof sorgente.prendiLotto === 'function' && Number.isFinite(sorgente.totale)) {
    return sorgente;
  }
  throw new Error('Sorgente immagini non valida: serve un array di File o { totale, prendiLotto }');
};

/**
 * Un lotto al server, con un secondo tentativo se la rete o il server
 * cadono. Torna { testi: Map, perse: number[] }. Lancia solo per errori che
 * un secondo tentativo non cura (400 per lotto troppo grande, 413).
 */
const inviaLotto = async (pagine, { tentativi = 2 } = {}) => {
  const images = pagine.map((p) => ({
    image_data: p.image_data,
    mime_type: p.mime_type || 'image/jpeg',
    image_id: `p_${p.pagina}`,
  }));

  let ultimoErrore = null;
  for (let tentativo = 1; tentativo <= tentativi; tentativo++) {
    try {
      const response = await axios.post(
        `${API_URL}/api/process-images-batch`,
        {
          images,
          parallel: true,
          max_batch_size: PAGINE_PER_LOTTO,
          enable_caching: true,
          return_partial_results: true
        },
        {
          headers: { "Content-Type": "application/json" },
          // Timeout dinamico basato sul numero di immagini
          timeout: 30000 + (images.length * 2000),
        }
      );

      const results = response.data?.results;
      if (!Array.isArray(results)) throw new Error('Risposta non valida dal server');

      const testi = new Map();
      const perse = [];
      pagine.forEach((p) => {
        const esito = results.find((r) => r.image_id === `p_${p.pagina}`);
        if (esito && esito.success && esito.extracted_text) {
          testi.set(p.pagina, esito.extracted_text);
        } else {
          perse.push(p.pagina);
        }
      });
      return { testi, perse };
    } catch (errore) {
      ultimoErrore = errore;
      const stato = errore.response?.status;
      const messaggio = errore.response?.data?.error || errore.response?.data?.detail || errore.message || '';

      if (stato === 413 || messaggio.includes('too large')) {
        throw new Error('Il file è troppo grande per essere elaborato. Prova a ridurre la qualità delle immagini o dividere il documento.');
      }
      if (messaggio.includes('Massimo') && messaggio.includes('immagini')) {
        throw new Error(messaggio);
      }
      const riprovabile = !stato || stato >= 500 || errore.code === 'ECONNABORTED';
      if (!riprovabile) throw new Error(messaggio || 'Errore elaborazione immagini');
      console.warn(`Lotto pagine ${pagine[0]?.pagina}-${pagine[pagine.length - 1]?.pagina}, tentativo ${tentativo} fallito:`, messaggio);
    }
  }

  // Due tentativi caduti: il lotto e' perso, gli altri vanno avanti.
  console.error('Lotto perso dopo i tentativi:', ultimoErrore?.message);
  return { testi: new Map(), perse: pagine.map((p) => p.pagina) };
};

/**
 * OCR di piu' immagini, a lotti, delegato al backend.
 *
 * @param {File[]|{totale:number, prendiLotto:(da:number,a:number)=>Promise<Array<{pagina:number,image_data:string,mime_type?:string}>>}} sorgente
 * @param {Object} options
 * @param {Function} [options.onProgress]   { phase, progress, message }
 * @param {Function} [options.onStatusUpdate] { status, message, progress }
 * @param {Function} [options.onParziale]   { perse, totali, pagine } se qualche pagina non e' tornata
 * @returns {Promise<string>} i testi ricuciti in ordine di pagina
 */
const processMultipleImages = async (sorgente, options = {}) => {
  const { onProgress, onStatusUpdate, onParziale } = options;

  // Il vecchio sistema (una chiamata per immagine) resta raggiungibile col
  // flag di emergenza, ma solo per gli array: le pagine di un PDF esistono
  // solo a lotti.
  if (Array.isArray(sorgente) && !isNewBatchSystemEnabled()) {
    console.log('📦 Usando sistema legacy per elaborazione immagini (batch locale)');
    return await processMultipleImagesLegacy(sorgente, options);
  }

  const lotti = normalizzaSorgente(sorgente, onProgress);
  const totale = lotti.totale;
  if (!totale) throw new Error('Nessuna immagine da elaborare');

  console.log(`🚀 OCR a lotti: ${totale} pagine, ${PAGINE_PER_LOTTO} per lotto`);
  if (onStatusUpdate) {
    onStatusUpdate({ status: 'preparing', message: `Preparazione di ${totale} pagine per l'elaborazione...`, progress: 0 });
  }

  const testi = new Map();
  const perse = [];

  // La resa del lotto successivo parte PRIMA di aspettare la risposta del
  // lotto corrente. Il `.catch` serve a non lasciare un rifiuto orfano mentre
  // la richiesta e' in volo: l'errore si rilegge quando il lotto viene atteso.
  const prendi = (da) => lotti.prendiLotto(da, fineLotto(da, totale)).catch((errore) => ({ errore }));

  let prossimo = prendi(1);
  for (let da = 1; da <= totale; da += PAGINE_PER_LOTTO) {
    const a = fineLotto(da, totale);
    const pronto = await prossimo;
    if (pronto && pronto.errore) throw pronto.errore;
    const daDopo = a + 1;
    prossimo = daDopo <= totale ? prendi(daDopo) : null;

    if (onStatusUpdate) {
      onStatusUpdate({
        status: 'uploading',
        message: `Elaborazione pagine ${da}-${a} di ${totale}...`,
        progress: Math.round(((da - 1) / totale) * 100)
      });
    }

    const esito = await inviaLotto(pronto);
    esito.testi.forEach((testo, pagina) => testi.set(pagina, testo));
    perse.push(...esito.perse);

    if (onProgress) {
      onProgress({
        phase: 'processing',
        progress: Math.round((a / totale) * 100),
        message: `Lette ${a} pagine su ${totale}`
      });
    }
  }

  if (perse.length / totale > QUOTA_MASSIMA_PAGINE_PERSE) {
    throw new Error(`Elaborazione fallita: ${perse.length} pagine su ${totale} non sono state lette`);
  }

  if (testi.size === 0) {
    throw new Error('Nessun testo estratto dalle immagini');
  }

  if (perse.length > 0) {
    console.warn(`⚠️ ${perse.length} pagine non elaborate:`, perse);
    if (onParziale) onParziale({ perse: perse.length, totali: totale, pagine: perse.sort((x, y) => x - y) });
  }

  if (onStatusUpdate) {
    onStatusUpdate({ status: 'completed', message: `✅ ${testi.size} pagine elaborate con successo`, progress: 100 });
  }

  return ricuciPagine(testi);
};

/**
 * Un'immagine ridotta a 1536 px sul lato lungo e compressa in JPEG (0,6),
 * in base64 senza prefisso. A 640 px l'OCR perdeva caratteri su manoscritti
 * e foto; 1536 tiene il testo leggibile restando nei limiti di Gemini.
 */
const comprimiImmagine = async (file) => {
  const MAX_DIMENSION = 1536;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Le HEIC che il browser non apre si convertono qui (STU-450).
  const { img, rilascia } = await apriFoto(file);

  let width = img.width;
  let height = img.height;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.floor(width * ratio);
    height = Math.floor(height * ratio);
  }

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(img, 0, 0, width, height);

  const base64Image = canvas.toDataURL('image/jpeg', 0.60).split(',')[1];
  rilascia();
  return base64Image;
};

// Funzione fallback per elaborazione con endpoint singolo
const processImagesWithFallback = async (imageFiles, onProgress) => {
  console.log("🔄 Fallback: elaborazione immagini con endpoint singolo...");
  const results = [];

  for (let i = 0; i < imageFiles.length; i++) {
    if (onProgress) {
      onProgress({
        phase: 'fallback',
        progress: Math.round((i / imageFiles.length) * 100),
        message: `Elaborazione immagine ${i + 1} di ${imageFiles.length}`
      });
    }

    try {
      const text = await processImage(imageFiles[i]);
      if (text) {
        results.push(text);
      }
    } catch (error) {
      console.error(`Errore elaborazione immagine ${i + 1}:`, error);
      // Continua con le altre immagini
    }
  }

  if (results.length === 0) {
    throw new Error("fallback failed: nessuna immagine elaborata con successo");
  }

  return results.join('\n\n--- Pagina successiva ---\n\n');
};

// Funzione di ultimo fallback per elaborazione singola
const processImagesSingularly = async (imageFiles) => {
  console.log("⚠️ Ultimo tentativo: elaborazione sequenziale delle immagini...");
  const results = [];

  for (const file of imageFiles) {
    try {
      const text = await processImage(file);
      if (text) {
        results.push(text);
      }
    } catch (error) {
      console.error(`Impossibile elaborare ${file.name}:`, error);
    }
  }

  if (results.length === 0) {
    throw new Error("Impossibile elaborare nessuna immagine");
  }

  return results.join('\n\n---\n\n');
};

// Funzione legacy per mantenere retrocompatibilità (batch locale da 20)
const processMultipleImagesLegacy = async (imageFiles, options = {}) => {
  const BATCH_SIZE = 20;
  const { onProgress, onStatusUpdate } = options;

  try {
    if (imageFiles.length > BATCH_SIZE) {
      console.log(`📦 [LEGACY] Divisione di ${imageFiles.length} immagini in batch da ${BATCH_SIZE}...`);

      const allResults = [];
      const totalBatches = Math.ceil(imageFiles.length / BATCH_SIZE);

      for (let i = 0; i < imageFiles.length; i += BATCH_SIZE) {
        const batch = imageFiles.slice(i, i + BATCH_SIZE);
        const batchNum = Math.floor(i / BATCH_SIZE) + 1;

        console.log(`🚀 [LEGACY] Elaborazione batch ${batchNum}/${totalBatches} (${batch.length} immagini)...`);

        if (onStatusUpdate) {
          onStatusUpdate({
            status: 'processing',
            message: `Elaborazione batch ${batchNum} di ${totalBatches}...`,
            progress: Math.round((i / imageFiles.length) * 100)
          });
        }

        try {
          // Processa ogni immagine del batch singolarmente (vecchio metodo)
          const batchResults = await Promise.all(
            batch.map(async (file, index) => {
              try {
                const text = await processImage(file);
                if (onProgress) {
                  onProgress({
                    phase: 'processing',
                    progress: Math.round(((i + index + 1) / imageFiles.length) * 100),
                    message: `Immagine ${i + index + 1} di ${imageFiles.length}`
                  });
                }
                return text;
              } catch (err) {
                console.error(`[LEGACY] Errore immagine ${i + index + 1}:`, err);
                return null;
              }
            })
          );

          const validResults = batchResults.filter(r => r !== null);
          if (validResults.length > 0) {
            allResults.push(validResults.join('\n\n'));
          }
        } catch (batchError) {
          console.error(`[LEGACY] Errore nel batch ${batchNum}:`, batchError);
        }
      }

      if (onStatusUpdate) {
        onStatusUpdate({
          status: 'completed',
          message: `✅ ${imageFiles.length} immagini elaborate con metodo legacy`,
          progress: 100
        });
      }

      return allResults.join('\n\n=== BATCH SUCCESSIVO ===\n\n');

    } else {
      // Batch singolo per 20 o meno immagini
      console.log(`🚀 [LEGACY] Elaborazione di ${imageFiles.length} immagini...`);

      const results = await Promise.all(
        imageFiles.map(async (file, index) => {
          try {
            const text = await processImage(file);
            if (onProgress) {
              onProgress({
                phase: 'processing',
                progress: Math.round(((index + 1) / imageFiles.length) * 100),
                message: `Immagine ${index + 1} di ${imageFiles.length}`
              });
            }
            return text;
          } catch (err) {
            console.error(`[LEGACY] Errore immagine ${index + 1}:`, err);
            return null;
          }
        })
      );

      const validResults = results.filter(r => r !== null);

      if (onStatusUpdate) {
        onStatusUpdate({
          status: 'completed',
          message: `✅ ${validResults.length} immagini elaborate con metodo legacy`,
          progress: 100
        });
      }

      if (validResults.length === 0) {
        throw new Error("[LEGACY] Nessuna immagine elaborata con successo");
      }

      return validResults.join('\n\n---\n\n');
    }
  } catch (error) {
    console.error("[LEGACY] Errore nel processamento batch:", error);
    throw error;
  }
};

/**
 * Genera un'infografica AI basata sul contenuto del documento.
 * Utilizza Google Nano Banana Pro per la generazione immagini.
 *
 * @param {string} documentText - Contenuto del documento da visualizzare
 * @param {string} documentId - ID del documento (opzionale)
 * @param {string} documentTitle - Titolo del documento (opzionale)
 * @param {string} userId - ID utente per salvare su Firebase (opzionale)
 * @param {boolean} skipFirebaseUpload - Se true, non salva su Firebase Storage
 * @param {Object} config - Configurazione infografica
 * @param {string} config.style - Stile: 'educational' | 'modern' | 'minimalist'
 * @param {string} config.aspectRatio - Formato: '16:9' | '9:16' | '1:1'
 * @param {string} config.language - Lingua: 'it' | 'en' | 'es'
 * @param {boolean} isPremium - Se l'utente è premium (richiesto)
 * @returns {Promise<Object>} Risultato con image_url o errore
 */
const generateInfographic = async (
  documentText,
  documentId = null,
  documentTitle = null,
  userId = null,
  skipFirebaseUpload = false,
  config = {},
  isPremium = false
) => {
  try {
    devLog("Inizio generazione infografica con testo di lunghezza:", documentText.length);

    // Normalizza il config
    const normalizedConfig = {
      style: config.style || 'educational',
      aspectRatio: config.aspectRatio || '16:9',
      language: config.language || 'it',
      partNumber: config.partNumber || null,
      totalParts: config.totalParts || null
    };

    // Prepara il payload
    const payload = {
      document_text: documentText,
      document_title: documentTitle || '',
      style: normalizedConfig.style,
      aspect_ratio: normalizedConfig.aspectRatio,
      language: normalizedConfig.language,
      isPremium: isPremium,
      part_number: normalizedConfig.partNumber,
      total_parts: normalizedConfig.totalParts
    };

    devLog("Payload infografica:", payload);
    devLog("URL completo:", axiosInstance.defaults.baseURL + "/api/infographic/generate");

    // Chiamata API
    const response = await axiosInstance.post("/api/infographic/generate", payload);

    devLog("Risposta ricevuta dal server:", response.status);

    // Verifica che la risposta contenga l'immagine in base64
    if (response.data && response.data.success && response.data.image_base64) {
      try {
        // Converti il base64 in un Blob
        const byteCharacters = atob(response.data.image_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const imageBlob = new Blob([byteArray], { type: "image/png" });

        // Se skipFirebaseUpload è true, ritorna solo l'URL locale
        if (skipFirebaseUpload) {
          const localUrl = URL.createObjectURL(imageBlob);
          return {
            success: true,
            image_url: localUrl,
            local_only: true,
            config: normalizedConfig,
            prompt_used: response.data.prompt_used,
            document_id: documentId,
            document_title: documentTitle
          };
        }

        // Verifica autenticazione per upload Firebase
        const auth = getAuth();
        const currentUser = auth.currentUser;
        const effectiveUserId = userId || (currentUser ? currentUser.uid : null);

        if (!effectiveUserId) {
          devError("Utente non autenticato. Impossibile salvare su Firebase.");
          const localUrl = URL.createObjectURL(imageBlob);
          return {
            success: true,
            image_url: localUrl,
            local_only: true,
            config: normalizedConfig,
            prompt_used: response.data.prompt_used
          };
        }

        // Carica il file su Firebase Storage
        const filename = `infographic_${Date.now()}.png`;
        const storageRef = ref(
          storage,
          `infographics/${effectiveUserId}/${documentId || 'general'}/${filename}`
        );

        devLog("Tentativo di caricamento su Firebase Storage:", storageRef);

        // Carica il blob su Firebase Storage
        const uploadResult = await uploadBytes(storageRef, imageBlob);
        devLog("Upload completato:", uploadResult);

        // Ottieni l'URL di download
        const downloadURL = await getDownloadURL(uploadResult.ref);
        devLog("URL di download ottenuto:", downloadURL);

        // Salva i metadati su Firestore
        try {
          const userInfographicsRef = collection(
            firestore,
            "infographics",
            effectiveUserId,
            "userInfographics"
          );

          const infographicData = {
            imageUrl: downloadURL,
            filename: filename,
            createdAt: serverTimestamp(),
            documentId: documentId,
            documentTitle: documentTitle || '',
            userId: effectiveUserId,
            config: normalizedConfig,
            promptUsed: response.data.prompt_used || ''
          };

          const docRef = await addDoc(userInfographicsRef, infographicData);
          devLog("Metadati infografica salvati con ID:", docRef.id);

          return {
            success: true,
            image_url: downloadURL,
            firebase_doc_id: docRef.id,
            config: normalizedConfig,
            prompt_used: response.data.prompt_used,
            document_id: documentId,
            document_title: documentTitle
          };
        } catch (firestoreError) {
          devError("Errore salvataggio metadati Firestore:", firestoreError);
          // Ritorna comunque successo con l'URL di download
          return {
            success: true,
            image_url: downloadURL,
            config: normalizedConfig,
            prompt_used: response.data.prompt_used,
            firestore_error: true
          };
        }
      } catch (processingError) {
        devError("Errore elaborazione immagine:", processingError);
        throw processingError;
      }
    } else {
      // Errore dalla risposta del server
      const errorMessage = response.data?.error || "Errore sconosciuto nella generazione";
      devError("Errore dal server:", errorMessage);
      return {
        success: false,
        error: errorMessage,
        error_code: response.data?.error_code
      };
    }
  } catch (error) {
    devError("Errore generazione infografica:", error);

    // Gestisci errori HTTP specifici
    if (error.response) {
      const status = error.response.status;
      const errorDetail = error.response.data?.detail || error.message;

      if (status === 403) {
        return {
          success: false,
          error: "Questa funzionalità è riservata agli utenti Premium",
          error_code: "PREMIUM_REQUIRED"
        };
      } else if (status === 400) {
        return {
          success: false,
          error: errorDetail,
          error_code: "INVALID_INPUT"
        };
      }
    }

    return {
      success: false,
      error: error.message || "Errore durante la generazione dell'infografica"
    };
  }
};

/**
 * Maturita oral exam feedback with MIM rubrica scoring.
 *
 * @param {string} materia - Current subject being evaluated (e.g. 'matematica')
 * @param {Array<{question: string, correct_answer: string, answer: string, materia: string}>} questions
 * @param {string} phase - Phase ID (e.g. 'interrogazione', 'pcto', 'cittadinanza', 'collegamenti')
 * @returns {Promise<{evaluation, rubrica, overall_score, final_feedback}>}
 */
const getMaturitaFeedback = async (materia, questions, phase = 'interrogazione') => {
  try {
    const response = await axiosInstance.post(
      "/maturita-feedback",
      { materia, questions, phase },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`API request failed with status ${response.status}`);
    }
  } catch (error) {
    devError("Error getting maturita feedback:", error);
    throw error;
  }
};

/**
 * Get feedback for a single Maturita question (ripetizione mode).
 * Returns score (0-10), spoken feedback, positives, negatives, suggestions.
 *
 * @param {string} question - The question text
 * @param {string} correct_answer - The expected correct answer
 * @param {string} answer - The student's answer
 * @param {string} materia - Subject (e.g., 'matematica')
 * @param {string} [language='it'] - Feedback language (always Italian for Maturità)
 * @returns {Promise<{score: number, feedback: string, positives: Object, negatives: Object, suggestions: string}>}
 * @throws {ApiTimeoutError} If the request exceeds the timeout
 */
const getMaturitaSingleFeedback = async (question, correct_answer, answer, materia, language = 'it') => {
  try {
    // Single-question feedback is a short call (1 question / 1 answer): 30s is
    // ample under normal conditions yet low enough that a hung request can't
    // leave the Ripetizione UI stuck in 'processing' (mic + text input disabled)
    // forever — that freeze was a direct churn driver. withTimeout aborts the
    // request and raises ApiTimeoutError so the caller's catch can recover.
    const response = await withTimeout(
      ({ signal }) => axiosInstance.post(
        "/maturita-feedback-single",
        { question, correct_answer, answer, materia, language },
        {
          headers: {
            "Content-Type": "application/json",
          },
          signal,
        }
      ),
      '/maturita-feedback-single',
      30000
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`API request failed with status ${response.status}`);
    }
  } catch (error) {
    devError("Error getting maturita single feedback:", error);
    throw error;
  }
};

/**
 * Calculate readiness score for Maturita preparation.
 *
 * @param {Object} params
 * @param {string} params.user_id
 * @param {Array} params.exam_results - Recent exam sessions with scores
 * @param {string|null} params.exam_date - ISO date string
 * @param {string[]} params.materie - Student's subjects
 * @param {Object} params.documents_per_materia - { materia: count }
 * @returns {Promise<{overall_score, per_subject, days_until_exam, is_panic_mode, last_activity, decay_applied, suggestions}>}
 */
const getReadinessScore = async ({ user_id, exam_results, exam_date = null, materie, documents_per_materia }) => {
  try {
    const response = await axiosInstance.post(
      "/readiness-score",
      { user_id, exam_results, exam_date, materie, documents_per_materia },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`API request failed with status ${response.status}`);
    }
  } catch (error) {
    devError("Error getting readiness score:", error);
    throw error;
  }
};

/**
 * Generate AI exam questions for a Maturita subject.
 * Used when the student hasn't uploaded documents for that materia.
 *
 * @param {string} materia - Subject ID (e.g., 'matematica')
 * @param {string} indirizzo - School track ID (e.g., 'scientifico')
 * @param {number} count - Number of questions to generate (default 7)
 * @param {string|null} schoolType - liceiTradizionali school type ID (e.g., 'liceo_scientifico')
 * @returns {Promise<{questions: Array<{question, answer, topic}>, materia, indirizzo, content_source}>}
 */
const generateMaturitaQuestions = async (materia, indirizzo, count = 7, schoolType = null) => {
  try {
    const response = await axiosInstance.post(
      "/generate-maturita-questions",
      { materia, indirizzo, count, ...(schoolType ? { school_type: schoolType } : {}) },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`API request failed with status ${response.status}`);
    }
  } catch (error) {
    devError("Error generating maturita questions:", error);
    throw error;
  }
};

/**
 * Get AI evaluation of a voice-based maturita exam from transcript data.
 *
 * @param {Object} phaseTranscripts - Transcript data organized by phase
 * @param {string} indirizzo - School track ID
 * @param {string[]} materie - Student's subjects
 * @returns {Promise<{overall_score, rubrica, per_materia, final_feedback}>}
 */
const getMaturitaVoiceFeedback = async (phaseTranscripts, indirizzo, materie) => {
  try {
    const response = await axiosInstance.post(
      "/api/maturita-voice-feedback",
      { phase_transcripts: phaseTranscripts, indirizzo, materie },
      {
        timeout: 60000, // 60 seconds - transcript evaluation can take time
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting maturita voice feedback:", error);
    throw error;
  }
};

/**
 * Piano di studio fino alla data d'esame per un documento GIA' caricato
 * (STU-457, la riga "Quando hai l'esame?" del Ripasso).
 *
 * Prima esisteva con un'altra firma e nessuno la chiamava. Il testo non si
 * manda piu': il server lo rilegge dal documento (anche da Storage, per le
 * dispense grandi) invece di farsi spedire centinaia di KB dal browser. Il
 * token si', perche' il piano finisce nel calendario di chi lo chiede e il
 * server deve sapere chi e'.
 *
 * Non lancia mai: torna `{ ok: true, ...esito }` oppure `{ ok: false, motivo }`
 * con `motivo` fra `premium_required`, `timeout`, `forbidden`, `invalid_exam_date`
 * e `generic`. Chi la chiama decide cosa dire, non deve interpretare errori axios.
 */
const generateStudyPlan = async ({ documentId, examDate, documentTitle = null, userId = null } = {}) => {
  try {
    const headers = await withAuthHeaders();
    // `user_id` resta come ripiego per quando il token non c'e' (sessione che
    // si rinnova): finche' l'enforcement di STU-57 e' spento il server lo
    // accetta, e il documento deve comunque essere suo. Col token vince il token.
    let uid = userId;
    if (!uid) {
      try {
        uid = getAuth().currentUser?.uid || JSON.parse(localStorage.getItem("userId")) || null;
      } catch (_) {
        uid = null;
      }
    }
    const response = await withTimeout(
      ({ signal }) => axiosInstance.post("/api/generate-study-plan", {
        document_id: documentId,
        document_title: documentTitle,
        exam_date: examDate,
        user_id: uid,
      }, { signal, headers }),
      "/api/generate-study-plan"
    );
    const esito = response?.data || {};
    if (esito.error) return { ok: false, motivo: "generic", dettaglio: esito.error };
    return { ok: true, ...esito };
  } catch (error) {
    devError("Error generating study plan:", error);
    if (error instanceof ApiTimeoutError || error?.code === "API_TIMEOUT") {
      return { ok: false, motivo: "timeout" };
    }
    const dettaglio = error?.response?.data?.detail;
    const noti = ["premium_required", "forbidden", "invalid_exam_date"];
    return { ok: false, motivo: noti.includes(dettaglio) ? dettaglio : "generic" };
  }
};

/**
 * Genera (o rigenera) il glossario per un documento esistente.
 * Usato per backfillare documenti caricati prima dell'introduzione del glossario.
 *
 * @param {string} documentId - ID Firestore del documento
 * @param {string} userId - ID utente (per isolamento sessione AI)
 * @returns {Promise<{success: boolean, document_id: string, terms_count: number, error?: string}>}
 */
const generateGlossary = async (documentId, userId = null) => {
  try {
    if (!documentId) return { error: "Missing documentId" };
    const response = await axiosInstance.post("/api/generate-glossary", {
      document_id: documentId,
      user_id: userId,
    }, { timeout: 180000 });
    return response.data;
  } catch (error) {
    devError("Error generating glossary:", error);
    return { error: error?.response?.data?.detail || error.message };
  }
};

/**
 * Esami Orali: un documento con domande ma senza temi chiede al server di
 * classificarle (stesso prompt dell'upload, idempotente). Risponde
 * { status: 'already'|'classified'|'running'|'none', themes: [...] }.
 */
const classifyQuestionThemes = async (documentID) => {
  const response = await withTimeout(
    ({ signal }) => axiosInstance.post("/classify-question-themes", { documentID }, { signal }),
    '/classify-question-themes',
    120000
  );
  return response.data;
};

/**
 * Gemello di classifyQuestionThemes per la difficolta': un documento con
 * domande ma senza `difficulty_level` chiede al server di classificarle
 * (idempotente lato server). Risponde
 * { status: 'already'|'classified'|'running'|'none', classified: n }.
 * Usato dallo stadio (STU-387) e dal Ripasso (STU-389).
 */
const classifyQuestionDifficulty = async (documentID) => {
  const response = await withTimeout(
    ({ signal }) => axiosInstance.post("/classify-question-difficulty", { documentID }, { signal }),
    '/classify-question-difficulty',
    120000
  );
  return response.data;
};

export {
  classifyQuestionThemes,
  classifyQuestionDifficulty,
  processDocument,
  generateStudyPlan,
  generateGlossary,
  getFeedback,
  getFeedbackStream,
  transcribeAudio,
  processImage,
  processMultipleImages,  // NUOVA FUNZIONE AGGIUNTA
  PAGINE_PER_LOTTO,
  ricuciPagine,
  generateSpeech,
  getFeedbackWithAssistant,
  generatePodcast,
  generatePodcastSSE,
  generatePodcastOutline,
  generateInfographic,
  getMaturitaFeedback,
  getMaturitaSingleFeedback,
  getReadinessScore,
  generateMaturitaQuestions,
  getMaturitaVoiceFeedback,
  axiosInstance,
};
